import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInteraction,
  fetchTodayMeetings,
  markNotOccurred,
  reportInteraction,
  rescheduleInteraction,
} from "@/api/interactions";
import type {
  InteractionCreatePayload,
  InteractionNotOccurredPayload,
  InteractionReportPayload,
  InteractionReschedulePayload,
  TodayMeetingsQuery,
} from "@/api/types";

const TODAY_STALE = 30_000;

/**
 * F2 §E — cache invalidation. After every mutation, drop:
 *   - todayMeetings (the Today widget)
 *   - accountOverview(accountId) (Account 360 header / activity counters)
 *   - accountActivitySummary(accountId) (timeline)
 *   - homeSummary (KPI grid / action queue)
 */
function invalidateF2(qc: ReturnType<typeof useQueryClient>, accountId: string | null) {
  qc.invalidateQueries({ queryKey: ["todayMeetings"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  if (accountId) {
    qc.invalidateQueries({ queryKey: ["accountOverview", accountId] });
    qc.invalidateQueries({ queryKey: ["accountActivitySummary", accountId] });
  }
}

export function useTodayMeetings(q: TodayMeetingsQuery = {}) {
  const key = ["todayMeetings", q.date ?? null, !!q.includeOverdue, !!q.includeCompleted] as const;
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchTodayMeetings(q, signal),
    staleTime: TODAY_STALE,
  });
}

export function useCreateInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InteractionCreatePayload) => createInteraction(payload),
    onSuccess: (_data, vars) => invalidateF2(qc, vars.accountId),
  });
}

export function useReportInteraction(accountId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InteractionReportPayload }) =>
      reportInteraction(id, body),
    onSuccess: () => invalidateF2(qc, accountId),
  });
}

export function useNotOccurredInteraction(accountId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InteractionNotOccurredPayload }) =>
      markNotOccurred(id, body),
    onSuccess: () => invalidateF2(qc, accountId),
  });
}

export function useRescheduleInteraction(accountId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InteractionReschedulePayload }) =>
      rescheduleInteraction(id, body),
    onSuccess: () => invalidateF2(qc, accountId),
  });
}
